import { baseQueryOptions } from "./baseQueryOptions";

/**
 * Opções das queries que o socket mantém quentes: lista de chats, chat aberto e
 * timeline da conversa.
 *
 * SA-1252: o default do projeto é `refetchOnMount: false` + `staleTime` de 5min
 * — ótimo para telas de configuração, fatal aqui. Os listeners de socket viviam
 * dentro da página de Chats, então sair para outra aba e voltar em menos de 5min
 * mostrava a conversa sem as mensagens que chegaram no meio (evento entregue ao
 * browser, descartado por falta de listener, e nada rebuscado na volta).
 *
 * Os listeners subiram para o provider (`RealtimeSync`, não desmontam mais) e
 * isto é a rede de segurança INDEPENDENTE do socket: qualquer buraco que o
 * tempo real não cobrir é curado no próximo mount.
 *
 * `baseQueryOptions` global fica intocado de propósito — as outras telas não têm
 * esse requisito de frescor e pagariam carga sem motivo.
 */
export const REALTIME_MOUNT_FRESH_MS = 30_000;

/**
 * SA-1452: `"always"` refazia a lista recém-buscada a cada montagem, inclusive
 * a que o pré-carregamento do boot acabou de trazer, e todas as páginas ao
 * voltar para Conversas. Os listeners vivem no provider e mantêm o cache em
 * dia mesmo com a tela fora, então só refaz o que pode estar velho:
 * - invalidado: a reconexão do socket marca as queries de tempo real
 *   (`resyncRealtimeQueries`), e quem estava fora da tela cura aqui;
 * - mais de 30 s: a mesma rede de segurança da SA-1252 contra um evento
 *   perdido, só que sem pagar a lista inteira por montagem.
 */
/** O pedaço do estado da query que a decisão lê (aceita qualquer `Query`). */
type MountCandidate = {
  state: { data: unknown; isInvalidated: boolean; dataUpdatedAt: number };
};

export function realtimeRefetchOnMount(
  query: MountCandidate
): boolean | "always" {
  if (query.state.data === undefined) return "always";
  if (query.state.isInvalidated) return "always";
  return Date.now() - query.state.dataUpdatedAt > REALTIME_MOUNT_FRESH_MS
    ? "always"
    : false;
}

export const realtimeQueryOptions = {
  ...baseQueryOptions,
  refetchOnMount: realtimeRefetchOnMount,
} as const;

export type RealtimeQueryOptions = typeof realtimeQueryOptions;
